/* eslint-disable @typescript-eslint/no-unused-vars */
import { TEXT_LINK_FOCUS_RING } from '../../lib/a11yFocus'
import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { Button, InlineAlert, SearchableSelect, SearchInput } from '../../components/BaseComponents'
import routes from '../../components/Router/routes'
import PageHeadings from '../../components/BaseComponents/PageHeadings/PageHeadings'
import PageTemplate from '../../components/BaseComponents/PageTemplate/PageTemplate'
import Pagination from '../../components/BaseComponents/Pagination/Pagination'
import useApi from '../../hooks/useApi'
import SimpleSortTable, { TableColumn } from '../../components/BaseComponents/SimpleSortTable/SimpleSortTable'
import AdminCreateCompany from '../../components/Admin/CreateCompany/CreateCompany'
import { Link } from 'react-router-dom'
import Label from '../../components/BaseComponents/Label'
import { useDebouncedFilters } from '../../hooks/useDebouncedFilters'
import usePaginatedFetch from '../../hooks/usePaginatedFetch'
import useDataSort from '../../hooks/useDataSort'
import ReviewUpgradeRequestModal from './ReviewUpgradeRequestModal'
import AddToOrganisationModal from './AddToOrganisationModal'
import { getApiErrorMessage, getCSRFToken } from '../../lib/utils'
import { setUserAdminLocked } from '../../lib/adminUserLock'
import ConfirmationModal from '../../components/BaseComponents/ConfirmationModal'
import ResendInviteModal from '../../components/BaseComponents/ResendInviteModal/ResendInviteModal'
import useAddNotification from '../../hooks/useAddNotification'

const MAX_VISIBLE_ITEMS = 2

// Shows the first few items and "+N more" (full list on hover) so multi-membership users keep one-line rows.
function TruncatedList({ items }: { items: { id: string; label: string; to?: string }[] }) {
	const visible = items.slice(0, MAX_VISIBLE_ITEMS)
	const hiddenCount = items.length - visible.length
	return (
		<span title={items.map((item) => item.label).join(', ')}>
			{visible.map((item, index) => (
				<span key={item.id}>
					{index > 0 && ', '}
					{item.to ? (
						<Link className={`text-link ${TEXT_LINK_FOCUS_RING}`} to={item.to}>{item.label}</Link>
					) : item.label}
				</span>
			))}
			{hiddenCount > 0 && <span className="text-charcoal-300"> +{hiddenCount} more</span>}
		</span>
	)
}

const baseColumns: TableColumn[] = [
	{
		id: 'full_name',
		label: 'Name',
		sortable: true,
		// Backend already resolves this to the invitee's email when no name was collected
		// (AdminController.users()'s coalesce(full_name, email)) - the suffix here only adds the
		// visual indicator, matching the training-session users table (Sub-task I-extra2 §17).
		renderer: (key, row) => <span>{ row[key] }{ row.row_type === 'invitation' || row.row_type === 'supplyChainInvitation' ? <span className="text-charcoal-300"> (pending)</span> : '' }</span>,
	},
	{
		id: 'email',
		label: 'Email',
		sortable: true,
	},
	{
		id: 'email_verified_at',
		label: 'Email verified',
		sortable: true,
		// null (not false) on an invitation row, which has no account yet.
		renderer: (key, row) => <span>{ row.email_verified === null || row.email_verified === undefined ? '-' : row.email_verified ? 'Yes' : 'No' }</span>,
	},
	{
		id: 'status',
		label: 'Invitation status',
		// Lifecycle only (row_type/admin_locked) - training/standard is an organisation property, never
		// shown against a user (see docs/todo/user-email-uniqueness.md §11).
		// Overridden inside the component so the renderer can open the resend modal - see `columns` below.
		renderer: (key, row) => <>{row.row_type === 'invitation' || row.row_type === 'supplyChainInvitation' ? 'Invited' : row.admin_locked ? 'Disabled' : 'Active'}</>,
	},
	{
		id: 'requested_upgrade_at',
		label: 'Requested upgrade',
		sortable: true,
		// Overridden inside the component so the renderer can open the review modal - see `columns` below.
		renderer: (key, row) => <>{row[key] ? 'Requested' : ''}</>,
	},
	{
		id: 'training_session_name',
		label: 'Training session(s)',
		sortable: true,
		renderer: (key, row) => {
			const sessions: { id: string; name: string }[] = row.training_sessions ?? []
			return sessions.length ? <TruncatedList items={sessions.map((session) => ({ id: session.id, label: session.name }))} /> : <span>-</span>
		},
	},
	{
		id: 'name',
		label: 'Organisation(s)',
		sortable: true,
		renderer: (key, row) => {
			const organisations: { id: string; name: string }[] = row.organisations ?? []
			return organisations.length ? (
				<TruncatedList
					items={organisations.map((organisation) => ({
						id: organisation.id,
						label: organisation.name,
						to: `/admin/companies/${organisation.id}`,
					}))}
				/>
			) : <span>-</span>
		},
	},
	{
		id: 'admin_locked',
		label: 'Locked by admin',
		sortable: true,
		renderer: (key, row) => <span>{ row.row_type === 'user' ? (row[key] ? 'Yes' : 'No') : '-' }</span>
	},
	{
		id: 'user_locked',
		label: 'Locked by organisation',
		sortable: true,
		renderer: (key, row) => <span>{ row.row_type === 'user' ? (row[key] ? 'Yes' : 'No') : '-' }</span>
	},
	{
		id: 'last_locked_at',
		label: 'Last locked',
		sortable: true,
		renderer: (key, row) => <span>{ row.last_locked_at ? formatDate(row.last_locked_at) : '' }</span>,
	},
	{
		id: 'last_unlocked_at',
		label: 'Last unlocked',
		sortable: true,
		renderer: (key, row) => <span>{ row.last_unlocked_at ? formatDate(row.last_unlocked_at) : '' }</span>,
	},
	{
		id: 'last_login_at',
		label: 'Last login',
		sortable: true,
		renderer: (key, row) => <span>{ row.last_login_status === 'recorded' && row.last_login_at ? formatDate(row.last_login_at) : row.last_login_status === 'unknown' ? 'Unknown' : 'Never' }</span>,
	},
	{
		id: 'actions',
		label: 'Actions',
		// Overridden inside the component so the renderer can call the Resend/Remove handlers - see `columns` below.
		renderer: () => <></>,
	}
];

const emailVerifiedOptions = [
	{ value: 'any', label: 'Any' },
	{ value: 'yes', label: 'Yes' },
	{ value: 'no', label: 'No' },
]

const lastLoginOptions = [
	{ value: 'any', label: 'Any' },
	{ value: 'recorded', label: 'Recorded' },
	{ value: 'never', label: 'Never' },
	{ value: 'unknown', label: 'Unknown' },
]

// Date-only display (en-GB), matching the admin tables' other date columns.
const formatDate = (value: string) => new Date(value).toLocaleDateString('en-GB')

const invitationStatusOptions = [
	{ value: 'any', label: 'Any' },
	{ value: 'open', label: 'Invite open' },
	{ value: 'signedUp', label: 'Signed up' },
	{ value: 'disabled', label: 'Disabled' },
]

function AdminUsers() {
	const dataSort = useDataSort('full_name', 'asc');

	const api = useApi();
	const addNotification = useAddNotification()

	const [reviewingUser, setReviewingUser] = useState<any | null>(null);
	const [addingUser, setAddingUser] = useState<any | null>(null);

	const [removalCandidate, setRemovalCandidate] = useState<any | null>(null);
	const [resendCandidate, setResendCandidate] = useState<any | null>(null);
	// #873: owned here (not inside the dumb ResendInviteModal) so a provider/transport failure
	// never surfaces as an unhandled rejection, and a successful rotation can show the *new*
	// signup link instead of immediately closing.
	const [resendLoading, setResendLoading] = useState(false);
	const [resendError, setResendError] = useState<string | null>(null);
	const [resendResult, setResendResult] = useState<{ signupUrl: string, delivery?: 'sent' | 'unconfirmed' } | null>(null);
	const [verifyCandidate, setVerifyCandidate] = useState<any | null>(null);
	// Issue #884: onLockUser (the sole caller of lib/adminUserLock.ts's setUserAdminLocked in
	// this file) previously had no catch, so a rejecting PUT (including the now-rejecting
	// READ_ONLY_ACCESS/ACCESS_AUDIT_UNAVAILABLE codes - #857) produced an unhandled rejection
	// with no visible feedback.
	const [lockError, setLockError] = useState('');
	// Issue #884: onMarkVerified/onRemoveUser previously closed their confirmation modal (and,
	// for onRemoveUser, cleared the candidate) *before* the unguarded await, so a rejecting
	// POST/DELETE produced an unhandled rejection with the modal already gone and no visible
	// feedback. The modal now stays open on failure so the admin can see the error and retry or
	// cancel, and is only dismissed on confirmed success.
	const [verifying, setVerifying] = useState(false);
	const [verifyError, setVerifyError] = useState('');
	const [removing, setRemoving] = useState(false);
	const [removeError, setRemoveError] = useState('');

	// Recovery for the email-verification gate: an admin vouches for, or re-sends the link to, an account.
	const onResendVerification = async (userId: string) => {
		try {
			await api.post(`/admin/user/${userId}/resendVerification`, { _csrf: await getCSRFToken() });
		} catch {
			addNotification({
				body: "We could not confirm whether the verification email was sent. Check the user's inbox before trying again.",
			})
		}
	}

	const onMarkVerified = async (userId: string) => {
		setVerifying(true);
		setVerifyError('');
		try {
			await api.post(`/admin/user/${userId}/verifyEmail`, { _csrf: await getCSRFToken() });
			setVerifyCandidate(null);
			list.refetch();
		} catch (err: any) {
			setVerifyError(getApiErrorMessage(err, 'Failed to mark this email as verified. Please try again.'));
		} finally {
			setVerifying(false);
		}
	}

	const closeResendModal = () => {
		setResendCandidate(null);
		setResendError(null);
		setResendResult(null);
	}

	const onResendInvitation = async () => {
		if (!resendCandidate) {
			return;
		}
		setResendLoading(true);
		setResendError(null);
		try {
			const endpoint = resendCandidate.row_type === 'supplyChainInvitation'
				? 'adminInvitations/resendSupplyChain'
				: 'adminInvitations/resend';
			const body = await api.post(endpoint, {
				inviteId: resendCandidate.id,
				_csrf: await getCSRFToken(),
			});
			// #873: the invitation was already rotated server-side by the time this resolves -
			// show the new link rather than closing the modal out from under the admin.
			setResendResult({ signupUrl: body.invite.signupUrl, delivery: body.invite.delivery });
			list.refetch();
		} catch (err) {
			// #873: the server may have rotated the invitation before the response was lost.
			// Refresh the list and require the admin to close this message before another attempt.
			setResendError('We could not confirm the resend outcome. The invitation may have changed; check the refreshed list before trying again.');
			list.refetch();
		} finally {
			setResendLoading(false);
		}
	}

	const onLockUser = async (userId: string, locked: boolean) => {
		setLockError('');
		try {
			await setUserAdminLocked(api, userId, locked);
			list.refetch();
		} catch (err: any) {
			setLockError(getApiErrorMessage(err, 'Failed to update this user\'s lock status. Please try again.'));
		}
	}

	const onRemoveUser = async (userId: string) => {
		setRemoving(true);
		setRemoveError('');
		try {
			await api.delete(`/admin/user/${userId}`, {
				_csrf: await getCSRFToken(),
			});
			setRemovalCandidate(null);
			list.refetch();
		} catch (err: any) {
			setRemoveError(getApiErrorMessage(err, 'Failed to remove this user. Please try again.'));
		} finally {
			setRemoving(false);
		}
	}

	const columns: TableColumn[] = baseColumns.map((column) =>
		column.id === 'status'
			? {
				...column,
				renderer: (key, row) =>
					row.row_type === 'invitation' || row.row_type === 'supplyChainInvitation' ? (
						<Button
							type='Outlined'
							size='sm'
							icon='fa-solid fa-paper-plane'
							text={row.is_expired ? 'Renew invite' : 'Resend invite'}
							hasLeadingIcon
							isTableRowButton
							onClick={() => setResendCandidate(row)}
						/>
					) : (
						<>{row.admin_locked ? 'Disabled' : 'Active'}</>
					),
			}
			: column.id === 'requested_upgrade_at'
			? {
				...column,
				renderer: (key, row) => row[key] ? (
					<button
						type='button'
						className={`text-link ${TEXT_LINK_FOCUS_RING}`}
						onClick={() => setReviewingUser(row)}
					>
						Requested
					</button>
				) : <></>,
			}
			: column.id === 'actions'
				? {
					...column,
					renderer: (key, row) => (
						<div className='flex space-x-2'>
							<Button
								type='Outlined'
								size='sm'
								text='Remove'
								onClick={() => setRemovalCandidate(row)}
							/>
							{row.row_type === 'user' && row.email_verified === false && (
								<>
									<Button
										type='Outlined'
										size='sm'
										text='Resend verification'
										onClick={() => onResendVerification(row.id)}
									/>
									<Button
										type='Outlined'
										size='sm'
										text='Mark verified'
										onClick={() => setVerifyCandidate(row)}
									/>
								</>
							)}
							{row.row_type === 'user' && (
								<Button
									type='Outlined'
									size='sm'
									text={row.admin_locked ? 'Unlock' : 'Lock'}
									onClick={() => onLockUser(row.id, !row.admin_locked)}
								/>
							)}
							{row.row_type === 'user' && !row.has_active_membership && (
								<Button
									type='Outlined'
									size='sm'
									text='Add to organisation'
									onClick={() => setAddingUser(row)}
								/>
							)}
						</div>
					),
				}
				: column
	)

	const [filters, setFilters] = useState({
		name: '',
		email: '',
		companyName: '',
		trainingSessionName: '',
		emailVerified: 'any',
		lastLogin: 'any',
		invitationStatus: 'any',
	})

	// Debounced/flushed copy of `filters`: typing doesn't fetch until the debounce (or Search) applies it.
	const [appliedFilters, setAppliedFilters] = useState(filters);


	// QA fix item 6: no SSE infrastructure exists yet, so a user's pending -> active change would
	// otherwise only show after a manual reload; the hook's silent poll covers it.
	const list = usePaginatedFetch<any, any>({
		path: 'admin/users',
		query: { ...appliedFilters, ...dataSort.params },
		pageSize: 20,
		getRows: (response) => response.data,
		pollIntervalMs: 15000,
	});

	const { searchInProgress, flush: flushSearch } = useDebouncedFilters(filters, () => setAppliedFilters(filters))

	const [addCompanyModalOpen, setAddCompanyModalOpen] = useState<boolean>(false)

	return (
		<PageTemplate testid='add-accounting-periods-main'>
			<Helmet>
				<title>{routes.adminCompanies.name}</title>
			</Helmet>
			<PageHeadings title='Create Organisation' />

			<div className='block'>
				<Button
					type='Primary'
					text='Create a New Organisation'
					size='sm'
					hasTrailingIcon
					icon='fa-solid fa-plus'
					onClick={() => {
						setAddCompanyModalOpen(true)
					}}
					additionalClass='mt-4 mb-2'
				/>
				<AdminCreateCompany
					open={addCompanyModalOpen}
					onClose={() => setAddCompanyModalOpen(false)}
				/>
			</div>

			<ReviewUpgradeRequestModal
				user={
					reviewingUser
						? { id: reviewingUser.id, fullName: reviewingUser.full_name, email: reviewingUser.email }
						: null
				}
				onClose={() => setReviewingUser(null)}
				onReviewed={() => list.refetch()}
				onUnconfirmed={() => list.refetch()}
			/>
			<AddToOrganisationModal
				user={
					addingUser
						? { id: addingUser.id, fullName: addingUser.full_name, email: addingUser.email }
						: null
				}
				onClose={() => setAddingUser(null)}
				onAdded={() => list.refetch()}
			/>

			<div className="my-8">
				<div className="flex space-x-3">
					<SearchInput
						name="name"
						label="Name"
						placeholder="Jo Bloggs..."
						value={filters.name}
						onChange={e => setFilters({...filters, name: e.target.value})}
						onSearch={flushSearch}
						onClear={() => setFilters({...filters, name: ''})}
						isLoading={searchInProgress}
					/>
					<SearchInput
						name="email"
						label="Email"
						placeholder="name@example.com..."
						value={filters.email}
						onChange={e => setFilters({...filters, email: e.target.value})}
						onSearch={flushSearch}
						onClear={() => setFilters({...filters, email: ''})}
						isLoading={searchInProgress}
					/>
				</div>
				<div className="text-white mt-8 mb-2">
					Organisation filters
				</div>
				<div className="flex space-x-3 mb-3">
					<SearchInput
						name="trainingSessionName"
						label="Group / session"
						placeholder="Onboarding cohort..."
						value={filters.trainingSessionName}
						onChange={e => setFilters({...filters, trainingSessionName: e.target.value})}
						onSearch={flushSearch}
						onClear={() => setFilters({...filters, trainingSessionName: ''})}
						isLoading={searchInProgress}
					/>
					<SearchInput
						name="companyName"
						label="Organisation name"
						placeholder="iSumio..."
						value={filters.companyName}
						onChange={e => setFilters({...filters, companyName: e.target.value})}
						onSearch={flushSearch}
						onClear={() => setFilters({...filters, companyName: ''})}
						isLoading={searchInProgress}
					/>
				</div>
				<div className="flex space-x-3 my-3">
					<label>
						<Label className="block">Email verified</Label>
						<SearchableSelect
							isSearchable={false}
							options={emailVerifiedOptions}
							value={emailVerifiedOptions.find((option) => option.value === filters.emailVerified)}
							onChange={(option) => setFilters({...filters, emailVerified: option?.value ?? 'any'})}
						/>
					</label>

					<label>
						<Label className="block">Last login</Label>
						<SearchableSelect
							isSearchable={false}
							options={lastLoginOptions}
							value={lastLoginOptions.find((option) => option.value === filters.lastLogin)}
							onChange={(option) => setFilters({...filters, lastLogin: option?.value ?? 'any'})}
						/>
					</label>

					<label>
						<Label className="block">Invitation status</Label>
						<SearchableSelect
							isSearchable={false}
							options={invitationStatusOptions}
							value={invitationStatusOptions.find((option) => option.value === filters.invitationStatus)}
							onChange={(option) => setFilters({...filters, invitationStatus: option?.value ?? 'any'})}
						/>
					</label>

				</div>

				<Button
					type='Primary'
					text='Search'
					size='sm'
					hasTrailingIcon
					onClick={flushSearch}
					additionalClass='mt-4 mb-2'
				/>
			</div>
			{ /* #884: wrapped in a single fragment child to avoid a false-positive TS2746
			    overload-resolution error when PageTemplate's direct child count crosses this
			    TS/React types version's inference threshold (verified empirically; no behavior
			    change - renders identically to two separate top-level children). */ }
			<>
				{ lockError && <InlineAlert type='error' className='mb-4'>{ lockError }</InlineAlert> }
				{ /* Issue #884: usePaginatedFetch's `error` was already correctly tracked internally but
				    never read here, so a rejecting list GET silently left the table looking like a
				    genuinely empty list instead of a failed load. */ }
				{ list.error && (
					<InlineAlert type='error' className='mb-4'>
						<p>{ getApiErrorMessage(list.error, 'Failed to load users. Please try again.') }</p>
						<Button
							type='Primary'
							hasTrailingIcon={true}
							icon='fa-solid fa-rotate-right'
							text='Try again'
							additionalClass='mt-4'
							onClick={() => list.refetch()}
						/>
					</InlineAlert>
				)}
			</>
			<SimpleSortTable
				isLoading={list.loading || searchInProgress}
				columns={columns}
				data={list.rows}
				defaultSorting={dataSort.defaultSorting}
				onSortPress={dataSort.onChange}
			/>
			<Pagination 
				pagination={list.pagination}
				onPageChange={list.onPageChange}
				isLoading={list.loading}
			/>

			<ResendInviteModal
				open={!!resendCandidate}
				onCancel={closeResendModal}
				onConfirm={onResendInvitation}
				fullName={resendCandidate?.full_name}
				email={resendCandidate?.email}
				signupUrl={resendCandidate?.signup_url}
				isExpired={resendCandidate?.is_expired}
				loading={resendLoading}
				error={resendError}
				result={resendResult}
			/>

			<ConfirmationModal
				open={!!removalCandidate}
				onCancel={() => { setRemovalCandidate(null); setRemoveError(''); }}
				onConfirm={() => removalCandidate && onRemoveUser(removalCandidate.id)}
				title="Remove user?"
			>
				{ removalCandidate && (
					<div>
						<p className="text-white mt-8">Name: { removalCandidate.full_name}</p>
						<p className="text-white">Email: { removalCandidate.email}</p>
						<p className="text-white">
							{ removalCandidate.row_type === 'invitation'
								? 'This will revoke the open invitation.'
								: 'This permanently deletes the account and removes every organisation membership. Any organisation this leaves with no members is deleted if it is a training or sample organisation, or locked if it is a real organisation - organisations shared with other members are unaffected. This cannot be undone.'}
						</p>
						{ removing && <p className="text-white mt-4">Removing...</p> }
						{ removeError && <InlineAlert type='error' className='mt-4'>{ removeError }</InlineAlert> }
					</div>
				)}
			</ConfirmationModal>

			<ConfirmationModal
					open={!!verifyCandidate}
					onCancel={() => { setVerifyCandidate(null); setVerifyError(''); }}
					onConfirm={() => verifyCandidate && onMarkVerified(verifyCandidate.id)}
					title="Mark email as verified?"
				>
					{ verifyCandidate && (
						<div>
							<p className="text-white mt-8">Email: { verifyCandidate.email}</p>
							<p className="text-white">Only do this once you are sure the person controls this mailbox.</p>
							{ verifying && <p className="text-white mt-4">Verifying...</p> }
							{ verifyError && <InlineAlert type='error' className='mt-4'>{ verifyError }</InlineAlert> }
						</div>
					)}
			</ConfirmationModal>

		</PageTemplate>
	)
}
export default AdminUsers
